import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import LeaderboardPickScreen from '@/components/LeaderboardPickScreen';
import LeaderboardTable from '@/components/LeaderboardTable';
import TierBadge from '@/components/TierBadge';
import HallOfFame from '@/components/HallOfFame';
import LivePriceIndicator from '@/components/LivePriceIndicator';
import usePriceStream from '@/hooks/usePriceStream';
import { shouldAutoRefresh } from '@/lib/marketHoursPolling';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NextWeekEntryBanner({ entry, targetWeek }) {
  let mondayLabel = '';
  let fridayLabel = '';
  try {
    const open = new Date(targetWeek.lock_open_at);
    const close = new Date(targetWeek.lock_close_at);
    const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    mondayLabel = fmt(open);
    fridayLabel = fmt(close);
  } catch { /* noop */ }
  return (
    <div className="rounded-2xl border-2 border-sky-300 dark:border-sky-700 bg-sky-50/50 dark:bg-sky-950/20 p-4 mb-3" data-testid="next-week-entry-banner">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="w-5 h-5 text-sky-600 dark:text-sky-400" />
        <h3 className="text-sm font-bold">Queued for next week's race ({mondayLabel} – {fridayLabel})</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
        <Lock className="w-3 h-3" /> Starting prices snapshot Monday 9 AM ET · freezes Friday 4:15 PM ET
      </p>
      <div className="rounded-lg bg-card border border-border p-3">
        <p className="text-xs text-muted-foreground mb-1">Your entry</p>
        <p className="text-sm font-bold mb-1.5">{entry.watchlist_name}</p>
        <div className="flex flex-wrap gap-1">
          {(entry.tickers || []).map((t) => (
            <span key={t} className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300">
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function MyEntryBanner({ entry, week }) {
  return (
    <div className="rounded-2xl border-2 border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 mb-3" data-testid="my-entry-banner">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        <h3 className="text-sm font-bold">You're entered in {week.theme_name}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
        <Lock className="w-3 h-3" /> Locked Monday 9am ET · freezes Friday 4:15 PM ET
      </p>
      <div className="rounded-lg bg-card border border-border p-3">
        <p className="text-xs text-muted-foreground mb-1">Your entry</p>
        <p className="text-sm font-bold mb-1.5">{entry.watchlist_name}</p>
        <div className="flex flex-wrap gap-1">
          {(entry.tickers || []).map((t) => (
            <span key={t} className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300">
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function TierMiniWidget({ tierInfo }) {
  if (!tierInfo) return null;
  const next = tierInfo.next_threshold;
  const progress = next ? Math.min(100, (tierInfo.points / next) * 100) : 100;
  return (
    <div className="rounded-2xl border border-border bg-card p-4 mb-3" data-testid="tier-mini-widget">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Your tier</span>
          <TierBadge tier={tierInfo.tier} />
        </div>
        <div className="text-xs">
          <span className="font-bold tabular-nums">{tierInfo.points}</span>
          <span className="text-muted-foreground"> pts{next ? ` · ${next} for next tier` : ' · max tier'}</span>
        </div>
      </div>
      {next ? (
        <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-violet-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
      ) : null}
    </div>
  );
}

export default function LeaderboardTab() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState(null);
  const [targetWeek, setTargetWeek] = useState(null);
  const [myEntry, setMyEntry] = useState(null);
  const [nextWeekEntry, setNextWeekEntry] = useState(null);
  const [canEnter, setCanEnter] = useState(false);
  const [rows, setRows] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [tierInfo, setTierInfo] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const reload = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [meRes, lbRes, tierRes] = await Promise.all([
        axios.get(`${API}/community/leaderboard/me`, { withCredentials: true }),
        axios.get(`${API}/community/leaderboard/weekly`, { withCredentials: true }),
        axios.get(`${API}/community/me/tier`, { withCredentials: true }).catch(() => null),
      ]);
      setWeek(meRes.data.week);
      setTargetWeek(meRes.data.target_week || meRes.data.week);
      setMyEntry(meRes.data.entry);
      setNextWeekEntry(meRes.data.next_week_entry);
      setCanEnter(meRes.data.can_enter);
      setRows(lbRes.data.rows || []);
      if (tierRes) setTierInfo(tierRes.data);
      setLastUpdated(Date.now());
      // Fetch the user's watchlists whenever they're eligible to enter
      // (either this week OR queue for next week).
      if (meRes.data.can_enter) {
        const wlRes = await axios.get(`${API}/community/watchlists/mine`, { withCredentials: true });
        setWatchlists(wlRes.data.watchlists || []);
      }
    } catch (e) {
      if (!silent) toast.error('Failed to load leaderboard.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Round 84k Phase C scaffolding (Feb 15, 2026) — subscribe to live
  // last-trade prices for EVERY ticker across EVERY visible entry on
  // the leaderboard. Memoised by the flattened sorted ticker key so
  // identical lists don't re-tear the SSE. When Phase C activates
  // (`MASSIVE_WEBSOCKET_ENABLED=true`), this stream will drive the
  // real-time recalculation of each entry's % return and reordering
  // of the leaderboard — exactly per the user's explicit Feb 15
  // requirement that "leaderboard race needs to update and also
  // update the total return in real time as prices change."
  const leaderboardSubTickers = useMemo(() => {
    const set = new Set();
    for (const row of rows) {
      for (const t of (row.tickers || [])) set.add(String(t || '').toUpperCase());
    }
    if (myEntry?.tickers) for (const t of myEntry.tickers) set.add(String(t || '').toUpperCase());
    if (nextWeekEntry?.tickers) for (const t of nextWeekEntry.tickers) set.add(String(t || '').toUpperCase());
    return Array.from(set).filter(Boolean).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rows.map(r => (r.tickers || []).join(',')).join('|'),
    (myEntry?.tickers || []).join(','),
    (nextWeekEntry?.tickers || []).join(','),
  ]);
  const { status: leaderboardStreamStatus } = usePriceStream(leaderboardSubTickers);

  // Round 84k Phase 3 — auto-refresh prices every 5 minutes so the
  // Total Return Race rows show fresh gains while the week is live.
  // Skips silently if the week is frozen (Friday 4pm ET +) to avoid
  // hammering the API for static data.
  //
  // Round 84k Phase 3.2 — only fire in production AND only during
  // the 9:30 AM ET → 4:15 PM ET market window. The gate also checks
  // the host so the preview environment never auto-polls.
  useEffect(() => {
    if (!week) return undefined;
    if (week.is_results_frozen) return undefined;
    if (!shouldAutoRefresh()) return undefined;
    const id = setInterval(() => { reload(true); }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [week, reload]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="leaderboard-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading leaderboard…
      </div>
    );
  }

  if (!week) {
    return (
      <div className="text-sm text-muted-foreground text-center py-8">
        Couldn't load this week's challenge.
      </div>
    );
  }

  return (
    <div data-testid="leaderboard-tab">
      <div className="flex items-center justify-end mb-2">
        <LivePriceIndicator status={leaderboardStreamStatus} />
      </div>
      <TierMiniWidget tierInfo={tierInfo} />
      {myEntry ? <MyEntryBanner entry={myEntry} week={week} /> : null}
      {nextWeekEntry ? <NextWeekEntryBanner entry={nextWeekEntry} targetWeek={targetWeek} /> : null}
      {canEnter ? (
        <div className="mb-4">
          <LeaderboardPickScreen week={week} targetWeek={targetWeek} watchlists={watchlists} onPicked={reload} />
        </div>
      ) : null}
      <LeaderboardTable week={week} rows={rows} myUserId={user?.user_id} lastUpdated={lastUpdated} onManualRefreshed={() => reload(true)} />
      <HallOfFame />
    </div>
  );
}
